import { isConversationAttachment } from './direct-call-attachments';
import type { ConversationAttachment } from './direct-call-attachments';
import type {
  DirectCallConnection,
  DirectCallMessage,
} from './direct-call-session';

export interface ConversationEvent {
  conversationId: string;
  messageId: string;
  turnId: number;
  humanTurnId: number;
  role: 'human' | 'seat';
  senderInstanceId: string;
  memberSetRevision: number;
  content: string;
  /** WI 35510: present only on a turn that carries attachments. */
  attachments?: ConversationAttachment[];
}

export interface ConversationEventPage {
  events: ConversationEvent[];
  nextAfterTurnId: number;
  hasMore: boolean;
  /** Story 35511: membership changes recorded by the server, beside the events. */
  memberChanges?: ConversationMemberChange[];
}

/** Story 35511: one owner membership change in the middle of a call. */
export interface ConversationMemberChange {
  conversationId: string;
  revision: number;
  afterTurnId: number;
  at: string;
  added: readonly string[];
  removed: readonly string[];
}

/** Sets a live call's members; resolves the server-confirmed set. */
export type ConversationMembersUpdate = (
  members: readonly string[],
  signal: AbortSignal
) => Promise<readonly string[]>;

function validChange(value: ConversationMemberChange, id: string): boolean {
  const ids = (list: unknown) =>
    Array.isArray(list) &&
    list.every((seat) => typeof seat === 'string' && seat.trim());
  return (
    !!value &&
    value.conversationId === id &&
    Number.isSafeInteger(value.revision) &&
    value.revision > 0 &&
    Number.isSafeInteger(value.afterTurnId) &&
    value.afterTurnId >= 0 &&
    typeof value.at === 'string' &&
    ids(value.added) &&
    ids(value.removed)
  );
}

// The implementation must use the signed-in HUMAN bearer, not the bridge Agent
// interceptor. These ports contain no user id or caller-selected role.
export interface HumanConversationApi {
  readEvents(
    id: string,
    after: number,
    signal: AbortSignal
  ): Promise<ConversationEventPage>;
  postEvent(
    id: string,
    content: string,
    signal: AbortSignal,
    to?: readonly string[],
    attachmentIds?: readonly string[]
  ): Promise<ConversationEvent>;
}

export interface HumanConversationNotifications {
  subscribe(
    changed: (conversationId: string) => void,
    reconnected: () => void,
    failed: () => void
  ): () => void;
  close(): void;
}

export function eventMessage(
  value: ConversationEvent,
  id: string,
  members: readonly string[],
  historical = false
): DirectCallMessage {
  const attachments = value?.attachments;
  const hasAttachments =
    Array.isArray(attachments) &&
    attachments.length > 0 &&
    attachments.every(isConversationAttachment);
  if (
    !value ||
    value.conversationId !== id ||
    !value.messageId?.trim() ||
    typeof value.content !== 'string' ||
    (attachments !== undefined && !hasAttachments) ||
    (!value.content.trim() && !hasAttachments) ||
    !value.senderInstanceId?.trim() ||
    !Number.isSafeInteger(value.turnId) ||
    value.turnId <= 0 ||
    !Number.isSafeInteger(value.humanTurnId) ||
    value.humanTurnId <= 0 ||
    !Number.isSafeInteger(value.memberSetRevision) ||
    value.memberSetRevision < 0 ||
    (value.role !== 'human' && value.role !== 'seat') ||
    (value.role === 'seat' &&
      !historical &&
      !members.includes(value.senderInstanceId))
  ) {
    throw new Error('Invalid conversation event');
  }
  return {
    id: value.messageId,
    conversationId: id,
    role: value.role === 'human' ? 'user' : 'assistant',
    content: value.content,
    senderInstanceId:
      value.role === 'seat' ? value.senderInstanceId : undefined,
    turnId: value.turnId,
    humanTurnId: value.humanTurnId,
    memberSetRevision: value.memberSetRevision,
    ...(hasAttachments
      ? { attachments: attachments.map((a) => ({ ...a })) }
      : {}),
    ...(historical ? { historical: true } : {}),
  };
}

/** Push wakes ordered replay; it never establishes ordering or advances a checkpoint. */
export class OrderedCallConnection implements DirectCallConnection {
  private readonly lifetime = new AbortController();
  private checkpoint = 0;
  private highestHumanTurn = 0;
  private replay: Promise<void> | null = null;
  private dirty = false;
  private ended = false;
  private stopNotifications?: () => void;
  private receive?: (value: DirectCallMessage) => void;
  private failed?: () => void;
  private membersChanged?: () => void;
  private current: string[];
  // Every seat that has been a member, removed seats included: a reply a
  // removed seat already committed must not fail the call (Story 35511).
  private readonly everSeats: Set<string>;
  private changes: ConversationMemberChange[] = [];
  private history: DirectCallMessage[] = [];
  private hydrating = false;

  constructor(
    readonly id: string,
    members: readonly string[],
    private readonly api: HumanConversationApi,
    private readonly notifications: HumanConversationNotifications,
    private readonly update?: ConversationMembersUpdate
  ) {
    this.current = [...members];
    this.everSeats = new Set(members);
  }

  get members(): readonly string[] {
    return [...this.current];
  }

  get seats(): readonly string[] {
    return [...this.everSeats];
  }

  get memberChanges(): readonly ConversationMemberChange[] {
    return this.changes.map((c) => ({
      ...c,
      added: [...c.added],
      removed: [...c.removed],
    }));
  }

  /** Story 35511: replace the call's seats; the old set stays on failure. */
  async setMembers(
    members: readonly string[],
    signal: AbortSignal
  ): Promise<readonly string[]> {
    const next = [...members];
    if (this.ended || !this.update || signal.aborted)
      throw new Error('Membership cannot change');
    const confirmed = await this.update(next, signal);
    if (this.ended || signal.aborted) throw new Error('Call has ended');
    if (
      !Array.isArray(confirmed) ||
      confirmed.length !== next.length ||
      new Set(confirmed).size !== next.length ||
      confirmed.some((seat) => !next.includes(seat))
    )
      throw new Error('Conversation membership was not confirmed');
    this.current = [...confirmed];
    for (const seat of confirmed) this.everSeats.add(seat);
    this.membersChanged?.();
    // Pick up the server's recorded change for the transcript.
    void this.catchUp().catch(() => this.reportFailure());
    return this.members;
  }

  /** Read an authorized, silent snapshot before exposing a resumed connection. */
  async hydrate(signal: AbortSignal): Promise<void> {
    if (this.receive || this.checkpoint || this.ended || this.hydrating)
      throw new Error('Connection is not available');
    const abort = () => this.close();
    signal.addEventListener('abort', abort, { once: true });
    this.hydrating = true;
    try {
      if (signal.aborted) abort();
      await this.catchUp();
      if (this.ended || signal.aborted) throw new Error('Call setup cancelled');
    } finally {
      this.hydrating = false;
      signal.removeEventListener('abort', abort);
    }
  }

  subscribe(
    receive: (message: DirectCallMessage) => void,
    failed: () => void,
    membersChanged?: () => void
  ): () => void {
    if (this.receive || this.ended)
      throw new Error('Connection is not available');
    this.receive = receive;
    this.failed = failed;
    this.membersChanged = membersChanged;
    const history = this.history;
    this.history = [];
    for (const message of history) {
      if (this.ended) return () => this.close();
      receive(message);
    }
    if (this.ended) return () => this.close();
    const wake = () => {
      void this.catchUp().catch(() => this.reportFailure());
    };
    this.stopNotifications = this.notifications.subscribe(
      (id) => {
        if (id === this.id) wake();
      },
      wake,
      () => this.reportFailure()
    );
    wake();
    return () => this.close();
  }

  async send(
    content: string,
    signal: AbortSignal,
    to?: readonly string[],
    attachmentIds: readonly string[] = []
  ): Promise<DirectCallMessage> {
    if (this.ended || signal.aborted || !this.receive)
      throw new Error('Call is closed');
    const operation = new AbortController();
    const abort = () => operation.abort();
    signal.addEventListener('abort', abort, { once: true });
    this.lifetime.signal.addEventListener('abort', abort, { once: true });
    try {
      // Never retry a POST after an unknown response: the input may have committed.
      const accepted = await this.api.postEvent(
        this.id,
        content,
        operation.signal,
        to,
        attachmentIds
      );
      const message = eventMessage(accepted, this.id, this.members);
      if (message.role !== 'user')
        throw new Error('Human input was not acknowledged');
      await this.catchUp();
      if (operation.signal.aborted || this.checkpoint < accepted.turnId)
        throw new Error('Input has not been reconciled');
      return message;
    } finally {
      signal.removeEventListener('abort', abort);
      this.lifetime.signal.removeEventListener('abort', abort);
    }
  }

  private catchUp(): Promise<void> {
    if (this.ended) return Promise.resolve();
    this.dirty = true;
    if (!this.replay) {
      this.replay = this.drain().then(
        () => {
          this.replay = null;
          // A wake can arrive after drain's last condition but before this
          // continuation. Include that work in the promise callers await.
          if (this.dirty && !this.ended) return this.catchUp();
        },
        (error) => {
          this.replay = null;
          throw error;
        }
      );
    }
    return this.replay;
  }

  private async drain(): Promise<void> {
    while (this.dirty && !this.ended) {
      this.dirty = false;
      const page = await this.api.readEvents(
        this.id,
        this.checkpoint,
        this.lifetime.signal
      );
      if (this.ended) return;
      if (
        !page ||
        !Array.isArray(page.events) ||
        page.events.length > 200 ||
        typeof page.hasMore !== 'boolean'
      )
        throw new Error('Invalid event page');
      // Validate the complete page before exposing it or advancing the durable position.
      // A seat this page's own changes name may speak in it: the change is
      // applied after the events, so the transcript keeps its order (review 64071).
      const seats = [...this.seats, ...this.pageSeats(page.memberChanges)];
      let next = this.checkpoint;
      let human = this.highestHumanTurn;
      const messages = page.events.map((event) => {
        const message = eventMessage(event, this.id, seats, this.hydrating);
        if (event.turnId !== next + 1)
          throw new Error('Conversation replay gap');
        if (event.role === 'human') {
          if (event.humanTurnId !== human + 1)
            throw new Error('Human turn gap');
          human = event.humanTurnId;
        } else if (event.humanTurnId > human)
          throw new Error('Reply precedes human input');
        next = event.turnId;
        return message;
      });
      if (page.nextAfterTurnId !== next || (page.hasMore && !messages.length))
        throw new Error('Invalid replay checkpoint');
      for (const message of messages) {
        if (this.ended) return;
        if (this.hydrating) this.history.push(message);
        else this.receive?.(message);
        this.checkpoint = message.turnId!;
        if (message.role === 'user')
          this.highestHumanTurn = message.humanTurnId!;
      }
      this.applyChanges(page.memberChanges);
      this.dirty ||= page.hasMore;
    }
  }

  // Story 35511: newer recorded changes, applied in revision order. A
  // malformed or foreign entry is ignored rather than failing the call.
  private applyChanges(changes: unknown): void {
    if (!Array.isArray(changes)) return;
    const last = this.changes.at(-1)?.revision ?? 0;
    const fresh = (changes as ConversationMemberChange[])
      .filter((c) => validChange(c, this.id) && c.revision > last)
      .sort((x, y) => x.revision - y.revision);
    if (!fresh.length) return;
    for (const c of fresh) {
      this.changes.push({ ...c, added: [...c.added], removed: [...c.removed] });
      this.current = [
        ...this.current.filter((seat) => !c.removed.includes(seat)),
        ...c.added.filter((seat) => !this.current.includes(seat)),
      ];
      // A removed seat stays known, so a reply it already committed is accepted
      // on a resumed call too.
      for (const seat of [...c.added, ...c.removed]) this.everSeats.add(seat);
    }
    this.membersChanged?.();
  }

  private pageSeats(changes: unknown): string[] {
    if (!Array.isArray(changes)) return [];
    return (changes as ConversationMemberChange[])
      .filter((c) => validChange(c, this.id))
      .flatMap((c) => [...c.added, ...c.removed]);
  }

  private reportFailure(): void {
    if (!this.ended) this.failed?.();
  }

  close(): void {
    if (this.ended) return;
    this.ended = true;
    this.lifetime.abort();
    this.receive = undefined;
    this.failed = undefined;
    this.membersChanged = undefined;
    this.history = [];
    try {
      this.stopNotifications?.();
    } finally {
      this.notifications.close();
    }
  }
}
